import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { addDays, format, startOfDay } from 'date-fns'
import { enUS, es } from 'date-fns/locale'

import { useHotelAvailability } from '@/components/HotelAvailabilityContext'
import { Calendar } from '@/shared/ui/calendar'

type PeluqueriaAvailabilityCalendarProps = {
    onSelect: (date: Date) => void
    selectedDate?: Date
}

export function PeluqueriaAvailabilityCalendar({
    onSelect,
    selectedDate: propSelectedDate,
}: PeluqueriaAvailabilityCalendarProps) {
    const [selectedDate, setSelectedDate] = useState<Date | undefined>(propSelectedDate || new Date())
    const { isHoliday, isWeekend: isWeekendDay } = useHotelAvailability()
    const { t, i18n } = useTranslation()

    const handleDateSelect = (date: Date | undefined) => {
        if (date) {
            setSelectedDate(date)
            onSelect(date)
        }
    }

    const isDateUnavailable = useCallback(
        (date: Date) => {
            const dateString = format(date, 'yyyy-MM-dd')
            const isWeekend = isWeekendDay(dateString)
            const isHolidayDay = isHoliday(dateString)

            return isWeekend || isHolidayDay
        },
        [isHoliday, isWeekendDay],
    )

    // Get the correct locale based on the current language
    const calendarLocale = i18n.language === 'en' ? enUS : es

    return (
        <div className='w-full space-y-6'>
            <Calendar
                fillSpace
                mode='single'
                selected={selectedDate}
                onSelect={handleDateSelect}
                className='rounded-md border'
                locale={calendarLocale}
                fromDate={startOfDay(new Date())}
                toDate={addDays(new Date(), 30)}
                disabled={isDateUnavailable}
                modifiers={{
                    available: date => !isDateUnavailable(date),
                    weekend: date => isWeekendDay(format(date, 'yyyy-MM-dd')),
                    holiday: date => isHoliday(format(date, 'yyyy-MM-dd')),
                }}
                modifiersStyles={{
                    weekend: { backgroundColor: 'red', color: 'white', cursor: 'not-allowed' },
                    holiday: { backgroundColor: '#fbbf24', color: 'white' },
                    busy: { backgroundColor: 'red', color: 'white', cursor: 'not-allowed' },
                }}
            />
            <div className='flex flex-wrap justify-center gap-4'>
                <div className='flex items-center'>
                    <div className='mr-2 h-4 w-4 rounded bg-red-500'></div>
                    <span className='text-sm'>{t('booking.step2.calendar.holidays', 'Festivos')}</span>
                </div>
                <div className='flex items-center'>
                    <div className='mr-2 h-4 w-4 rounded bg-gray-200'></div>
                    <span className='text-sm'>{t('booking.step2.calendar.notAvailable', 'No disponible')}</span>
                </div>
            </div>
        </div>
    )
}
